"use client";

import { c as _c } from "react/compiler-runtime";
import { useCompactMode } from "@/contexts/CompactModeContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropMentionedUser } from "@/generated/models/ApiDropMentionedUser";
import type { ApiMentionedWave } from "@/generated/models/ApiMentionedWave";
import { ApiDropType } from "@/generated/models/ApiDropType";
import type { ApiUpdateDropRequest } from "@/generated/models/ApiUpdateDropRequest";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useDropUpdateMutation } from "@/hooks/drops/useDropUpdateMutation";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useHasTouchInput from "@/hooks/useHasTouchInput";
import { selectEditingDropId, setEditingDropId } from "@/store/editSlice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createBreakpoint } from "react-use";
import type { DropInteractionParams } from "./Drop";
import { DropLocation } from "./Drop";
import type { BoostAnimationState } from "./DropBoostAnimation";
import DropBoostAnimation from "./DropBoostAnimation";
import WaveDropActions from "./WaveDropActions";
import WaveDropAuthorPfp from "./WaveDropAuthorPfp";
import WaveDropContent from "./WaveDropContent";
import WaveDropHeader from "./WaveDropHeader";
import WaveDropMetadata from "./WaveDropMetadata";
import WaveDropMobileMenu from "./WaveDropMobileMenu";
import WaveDropRatings from "./WaveDropRatings";
import WaveDropReactions from "./WaveDropReactions";
import WaveDropReply from "./WaveDropReply";
enum GroupingThreshold {
  TIME_DIFFERENCE = 60000,
}
const useBreakpoint = createBreakpoint({
  MD: 768,
  S: 0
});
const shouldGroupWithDrop = (currentDrop: ExtendedDrop, otherDrop: ExtendedDrop | null): boolean => {
  if (!otherDrop || currentDrop.parts.length > 1) {
    return false;
  }
  const isSameAuthor = currentDrop.author.handle === otherDrop.author.handle;
  const isWithinTimeThreshold = Math.abs(currentDrop.created_at - otherDrop.created_at) <= GroupingThreshold.TIME_DIFFERENCE;
  if (!isSameAuthor || !isWithinTimeThreshold) {
    return false;
  }
  const bothNotReplies = !currentDrop.reply_to && !otherDrop.reply_to;
  const repliesInSameThread = currentDrop.reply_to?.drop_id === otherDrop.reply_to?.drop_id;
  return bothNotReplies || repliesInSameThread;
};
const RANK_STYLES = {
  1: "tw-border-l tw-border-[#E8D48A]/40 tw-border-y-0 tw-border-r-0",
  2: "tw-border-l tw-border-[#DDDDDD]/40 tw-border-y-0 tw-border-r-0",
  3: "tw-border-l tw-border-[#CD7F32]/40 tw-border-y-0 tw-border-r-0",
  default: "tw-border-l tw-border-iron-600/40 tw-border-y-0 tw-border-r-0"
} as const;
const getColorClasses = ({
  isActiveDrop,
  rank,
  isDrop,
  location
}: {
  isActiveDrop: boolean;
  rank: number | null;
  isDrop: boolean;
  location: DropLocation;
}): string => {
  if (isActiveDrop) {
    return "tw-bg-[#3CCB7F]/10 tw-border-l tw-border-l-[#3CCB7F] tw-border-solid tw-border-y-0 tw-border-r-0";
  }
  if (!isDrop) {
    const isWaveView = location === DropLocation.WAVE;
    const hoverClass = isWaveView ? "desktop-hover:hover:tw-bg-iron-800/50" : "";
    const ringClasses = isWaveView ? "" : "tw-ring-1 tw-ring-inset tw-ring-iron-800";
    const bgClass = isWaveView ? "" : "tw-bg-iron-950/80";
    return `${bgClass} ${ringClasses} ${hoverClass}`.trim();
  }
  const rankClass = RANK_STYLES[rank as keyof typeof RANK_STYLES];
  return `${rankClass} tw-transition-shadow tw-duration-300`.trim();
};
const getDropClasses = (isActiveDrop: boolean, groupingClass: string, location: DropLocation, rank: number | null, isDrop: boolean): string => {
  const baseClasses = "touch-select-none tw-cursor-default tw-relative tw-group tw-w-full tw-flex tw-flex-col tw-px-4 tw-transition-colors tw-duration-300";
  const streamClasses = "tw-rounded-xl";
  const chatDropClasses = isDrop ? "tw-rounded-lg tw-my-0.5" : "";
  const rankClasses = getColorClasses({
    isActiveDrop,
    rank,
    isDrop,
    location
  });
  const locationClasses = location === DropLocation.MY_STREAM || location === DropLocation.PROFILE ? streamClasses : chatDropClasses;
  return `${baseClasses} ${groupingClass} ${locationClasses} ${rankClasses}`.trim();
};
interface WaveDropProps {
  readonly drop: ExtendedDrop;
  readonly previousDrop: ExtendedDrop | null;
  readonly nextDrop: ExtendedDrop | null;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly dropViewDropId: string | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onReplyClick: (serialNo: number) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly wrapContentOnly?: ((content: React.ReactNode) => React.ReactNode) | undefined;
}
const WaveDrop = t0 => {
  const $ = _c(142);
  const {
    drop,
    previousDrop,
    nextDrop,
    showWaveInfo,
    activeDrop,
    location,
    dropViewDropId,
    onReply,
    onReplyClick,
    onQuoteClick,
    onDropContentClick,
    showReplyAndQuote,
    wrapContentOnly
  } = t0;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const [boostAnimation, setBoostAnimation] = useState(null);
  const dropRef = useRef(null);
  const dispatch = useDispatch();
  const editingDropId = useSelector(selectEditingDropId);
  const isEditing = editingDropId === drop.id;
  const longPressTimeoutRef = useRef(null);
  const touchStartPosition = useRef(null);
  const dropUpdateMutation = useDropUpdateMutation();
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const isStorm = drop.parts.length > 1;
  const isDrop = drop.drop_type === ApiDropType.Participatory;
  let t1;
  if ($[0] !== drop || $[1] !== isDrop || $[2] !== previousDrop) {
    t1 = !isDrop && shouldGroupWithDrop(drop, previousDrop);
    $[0] = drop;
    $[1] = isDrop;
    $[2] = previousDrop;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const shouldGroupWithPreviousDrop = t1;
  let t2;
  if ($[4] !== drop || $[5] !== isDrop || $[6] !== nextDrop) {
    t2 = !isDrop && shouldGroupWithDrop(drop, nextDrop);
    $[4] = drop;
    $[5] = isDrop;
    $[6] = nextDrop;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const shouldGroupWithNextDrop = t2;
  const isMobile = useIsMobileDevice();
  const hasTouch = useHasTouchInput() || isMobile;
  const breakpoint = useBreakpoint();
  const isMdUp = breakpoint === "MD";
  const allowLongPress = hasTouch && !isMdUp;
  const compact = useCompactMode();
  const isProfileView = location === DropLocation.PROFILE;
  const showAuthorInfo = !shouldGroupWithPreviousDrop || isProfileView;
  let t3;
  if ($[8] !== isProfileView || $[9] !== shouldGroupWithNextDrop || $[10] !== shouldGroupWithPreviousDrop) {
    t3 = () => {
      if (isProfileView) {
        return "tw-py-4";
      }
      if (shouldGroupWithPreviousDrop) {
        return "tw-pt-1";
      }
      if (shouldGroupWithNextDrop) {
        return "tw-pt-4 tw-pb-1";
      }
      return "tw-py-4";
    };
    $[8] = isProfileView;
    $[9] = shouldGroupWithNextDrop;
    $[10] = shouldGroupWithPreviousDrop;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const getGroupingClass = t3;
  const groupingClass = getGroupingClass();
  const replyTo = drop.reply_to;
  const isGroupedReplyWithPrevious = shouldGroupWithPreviousDrop && replyTo?.drop_id === previousDrop?.reply_to?.drop_id;
  const shouldShowReplyHeader = !!replyTo && replyTo.drop_id !== dropViewDropId && !isGroupedReplyWithPrevious;
  let t4;
  if ($[12] !== allowLongPress || $[13] !== dispatch || $[14] !== editingDropId) {
    t4 = () => {
      if (!allowLongPress) {
        return;
      }
      if (editingDropId) {
        dispatch(setEditingDropId(null));
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[12] = allowLongPress;
    $[13] = dispatch;
    $[14] = editingDropId;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const handleLongPress = t4;
  let t5;
  if ($[16] !== allowLongPress || $[17] !== handleLongPress || $[18] !== isEditing) {
    t5 = e => {
      if (!allowLongPress) {
        return;
      }
      if (isEditing) {
        return;
      }
      const touch = e.touches[0];
      touchStartPosition.current = {
        x: touch.clientX,
        y: touch.clientY
      };
      longPressTimeoutRef.current = setTimeout(handleLongPress, 500);
    };
    $[16] = allowLongPress;
    $[17] = handleLongPress;
    $[18] = isEditing;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const handleTouchStart = t5;
  let t6;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
      }
      touchStartPosition.current = null;
    };
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const handleTouchEnd = t6;
  let t7;
  if ($[21] !== allowLongPress) {
    t7 = e_0 => {
      if (!allowLongPress) {
        return;
      }
      if (!touchStartPosition.current) {
        return;
      }
      const touch_0 = e_0.touches[0];
      const deltaX = Math.abs(touch_0.clientX - touchStartPosition.current.x);
      const deltaY = Math.abs(touch_0.clientY - touchStartPosition.current.y);
      if ((deltaX > 10 || deltaY > 10) && longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
      }
    };
    $[21] = allowLongPress;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const handleTouchMove = t7;
  let t8;
  if ($[23] !== activePartIndex || $[24] !== dispatch || $[25] !== drop || $[26] !== editingDropId || $[27] !== onReply) {
    t8 = () => {
      if (editingDropId) {
        dispatch(setEditingDropId(null));
      }
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex].part_id
      });
    };
    $[23] = activePartIndex;
    $[24] = dispatch;
    $[25] = drop;
    $[26] = editingDropId;
    $[27] = onReply;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  const handleOnReply = t8;
  let t9;
  if ($[29] !== dispatch || $[30] !== editingDropId) {
    t9 = () => {
      if (editingDropId) {
        dispatch(setEditingDropId(null));
      }
      setIsSlideUp(false);
    };
    $[29] = dispatch;
    $[30] = editingDropId;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  const handleOnAddReaction = t9;
  let t10;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = e_1 => {
      e_1.stopPropagation();
      setLongPressTriggered(false);
      setIsSlideUp(true);
    };
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  const handleOpenTouchActions = t10;
  let t11;
  if ($[33] !== dispatch || $[34] !== drop.id) {
    t11 = () => {
      setIsSlideUp(false);
      dispatch(setEditingDropId(drop.id));
    };
    $[33] = dispatch;
    $[34] = drop.id;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  const handleOnEdit = t11;
  let t12;
  if ($[36] !== activePartIndex || $[37] !== dispatch || $[38] !== drop || $[39] !== dropUpdateMutation) {
    t12 = (newContent, mentions, mentionedWaves) => {
      const cleanedMentions = (mentions ?? drop.mentioned_users).map(_temp);
      const cleanedWaves = (mentionedWaves ?? drop.mentioned_waves).map(_temp2);
      const updateRequest = {
        parts: drop.parts.map((part, index) => ({
          content: index === activePartIndex ? newContent : part.content,
          quoted_drop: part.quoted_drop ?? null,
          media: part.media
        })),
        title: drop.title,
        metadata: drop.metadata,
        referenced_nfts: drop.referenced_nfts,
        mentioned_users: cleanedMentions,
        mentioned_waves: cleanedWaves,
        signature: null
      };
      dispatch(setEditingDropId(null));
      dropUpdateMutation.mutate({
        dropId: drop.id,
        request: updateRequest,
        currentDrop: drop
      });
    };
    $[36] = activePartIndex;
    $[37] = dispatch;
    $[38] = drop;
    $[39] = dropUpdateMutation;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  const handleEditSave = t12;
  let t13;
  if ($[41] !== dispatch) {
    t13 = () => {
      dispatch(setEditingDropId(null));
    };
    $[41] = dispatch;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  const handleEditCancel = t13;
  let t14;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      setBoostAnimation(null);
    };
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  const handleBoostAnimationComplete = t14;
  let t15;
  if ($[44] !== drop.context_profile_context?.boosted || $[45] !== drop.id) {
    t15 = () => {
      if (!dropRef.current) {
        return;
      }
      const rect = dropRef.current.getBoundingClientRect();
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;
      const isBoosted = drop.context_profile_context?.boosted ?? false;
      setBoostAnimation({
        id: `${drop.id}-${Date.now()}`,
        x,
        y,
        type: isBoosted ? "unboost" : "boost"
      });
    };
    $[44] = drop.context_profile_context?.boosted;
    $[45] = drop.id;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  drop.context_profile_context?.boosted;
  const handleMobileBoostAnimation = t15;
  let t16;
  let t17;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => () => {
      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
      }
    };
    t17 = [];
    $[47] = t16;
    $[48] = t17;
  } else {
    t16 = $[47];
    t17 = $[48];
  }
  useEffect(t16, t17);
  const effectiveIsSlideUp = isSlideUp && !isEditing;
  const dropClasses = getDropClasses(isActiveDrop, groupingClass, location, drop.rank, isDrop);
  let t18;
  if ($[49] !== drop.wave || $[50] !== onReplyClick || $[51] !== replyTo || $[52] !== shouldShowReplyHeader) {
    t18 = shouldShowReplyHeader && <WaveDropReply onReplyClick={onReplyClick} dropId={replyTo.drop_id} dropPartId={replyTo.drop_part_id} maybeDrop={replyTo.drop ? {
      ...replyTo.drop,
      wave: drop.wave
    } : null} />;
    $[49] = drop.wave;
    $[50] = onReplyClick;
    $[51] = replyTo;
    $[52] = shouldShowReplyHeader;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== drop || $[55] !== showAuthorInfo) {
    t19 = showAuthorInfo && <WaveDropAuthorPfp drop={drop} />;
    $[54] = drop;
    $[55] = showAuthorInfo;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  const t20 = showAuthorInfo ? "calc(100% - 3.5rem)" : "100%";
  let t21;
  if ($[57] !== t20) {
    t21 = {
      maxWidth: t20
    };
    $[57] = t20;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== activePartIndex || $[60] !== drop || $[61] !== handleOpenTouchActions || $[62] !== hasTouch || $[63] !== isEditing || $[64] !== isStorm || $[65] !== showAuthorInfo || $[66] !== showReplyAndQuote || $[67] !== showWaveInfo) {
    t22 = showAuthorInfo && <WaveDropHeader drop={drop} showWaveInfo={showWaveInfo} isStorm={isStorm} currentPartIndex={activePartIndex} partsCount={drop.parts.length} showActionsButton={hasTouch && showReplyAndQuote && !isEditing} onOpenActions={handleOpenTouchActions} />;
    $[59] = activePartIndex;
    $[60] = drop;
    $[61] = handleOpenTouchActions;
    $[62] = hasTouch;
    $[63] = isEditing;
    $[64] = isStorm;
    $[65] = showAuthorInfo;
    $[66] = showReplyAndQuote;
    $[67] = showWaveInfo;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  const t23 = `tw-w-full ${shouldGroupWithPreviousDrop && !isProfileView ? "tw-pl-[3.25rem]" : ""}`;
  let t24;
  if ($[69] !== activePartIndex || $[70] !== allowLongPress || $[71] !== drop || $[72] !== dropUpdateMutation.isPending || $[73] !== handleEditCancel || $[74] !== handleEditSave || $[75] !== handleLongPress || $[76] !== isEditing || $[77] !== onDropContentClick || $[78] !== onQuoteClick) {
    t24 = <WaveDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onLongPress={handleLongPress} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} setLongPressTriggered={setLongPressTriggered} isEditing={isEditing} isSaving={dropUpdateMutation.isPending} onSave={handleEditSave} onCancel={handleEditCancel} hasTouch={allowLongPress} />;
    $[69] = activePartIndex;
    $[70] = allowLongPress;
    $[71] = drop;
    $[72] = dropUpdateMutation.isPending;
    $[73] = handleEditCancel;
    $[74] = handleEditSave;
    $[75] = handleLongPress;
    $[76] = isEditing;
    $[77] = onDropContentClick;
    $[78] = onQuoteClick;
    $[79] = t24;
  } else {
    t24 = $[79];
  }
  let t25;
  if ($[80] !== t23 || $[81] !== t24) {
    t25 = <div className={t23}>{t24}</div>;
    $[80] = t23;
    $[81] = t24;
    $[82] = t25;
  } else {
    t25 = $[82];
  }
  let t26;
  if ($[83] !== t21 || $[84] !== t22 || $[85] !== t25) {
    t26 = <div className="tw-flex tw-w-full tw-flex-col tw-gap-y-1" style={t21}>{t22}{t25}</div>;
    $[83] = t21;
    $[84] = t22;
    $[85] = t25;
    $[86] = t26;
  } else {
    t26 = $[86];
  }
  let t27;
  if ($[87] !== t19 || $[88] !== t26) {
    t27 = <div className="tw-relative tw-z-10 tw-flex tw-w-full tw-gap-x-3 tw-border-0 tw-bg-transparent tw-text-left">{t19}{t26}</div>;
    $[87] = t19;
    $[88] = t26;
    $[89] = t27;
  } else {
    t27 = $[89];
  }
  let t28;
  if ($[90] !== activePartIndex || $[91] !== drop || $[92] !== handleOnEdit || $[93] !== handleOnReply || $[94] !== isEditing || $[95] !== isMobile || $[96] !== showReplyAndQuote) {
    t28 = !isMobile && showReplyAndQuote && !isEditing && <WaveDropActions drop={drop} activePartIndex={activePartIndex} onReply={handleOnReply} onEdit={handleOnEdit} />;
    $[90] = activePartIndex;
    $[91] = drop;
    $[92] = handleOnEdit;
    $[93] = handleOnReply;
    $[94] = isEditing;
    $[95] = isMobile;
    $[96] = showReplyAndQuote;
    $[97] = t28;
  } else {
    t28 = $[97];
  }
  let t29;
  if ($[98] !== t18 || $[99] !== t27 || $[100] !== t28) {
    t29 = <>{t18}{t27}{t28}</>;
    $[98] = t18;
    $[99] = t27;
    $[100] = t28;
    $[101] = t29;
  } else {
    t29 = $[101];
  }
  const contentBlock = t29;
  const t30 = `tw-mx-2 tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 ${compact ? "tw-ml-11 tw-w-[calc(100%-2.5rem)]" : "tw-ml-[3.25rem] tw-w-[calc(100%-3.25rem)]"}`;
  let t31;
  if ($[102] !== drop.metadata) {
    t31 = drop.metadata.length > 0 && <WaveDropMetadata metadata={drop.metadata} />;
    $[102] = drop.metadata;
    $[103] = t31;
  } else {
    t31 = $[103];
  }
  let t32;
  if ($[104] !== drop) {
    t32 = !!drop.raters_count && <WaveDropRatings drop={drop} />;
    $[104] = drop;
    $[105] = t32;
  } else {
    t32 = $[105];
  }
  let t33;
  if ($[106] !== drop) {
    t33 = <WaveDropReactions drop={drop} />;
    $[106] = drop;
    $[107] = t33;
  } else {
    t33 = $[107];
  }
  let t34;
  if ($[108] !== t30 || $[109] !== t31 || $[110] !== t32 || $[111] !== t33) {
    t34 = <div className={t30}>{t31}{t32}{t33}</div>;
    $[108] = t30;
    $[109] = t31;
    $[110] = t32;
    $[111] = t33;
    $[112] = t34;
  } else {
    t34 = $[112];
  }
  const reactionsRow = t34;
  const t35 = `${isDrop && location === DropLocation.WAVE ? "tw-px-4 tw-py-0.5" : ""} ${isProfileView ? "tw-mb-3" : ""} tw-w-full`;
  let t36;
  if ($[113] !== contentBlock || $[114] !== wrapContentOnly) {
    t36 = wrapContentOnly ? wrapContentOnly(contentBlock) : contentBlock;
    $[113] = contentBlock;
    $[114] = wrapContentOnly;
    $[115] = t36;
  } else {
    t36 = $[115];
  }
  let t37;
  if ($[116] !== drop || $[117] !== effectiveIsSlideUp || $[118] !== handleMobileBoostAnimation || $[119] !== handleOnAddReaction || $[120] !== handleOnEdit || $[121] !== handleOnReply || $[122] !== longPressTriggered || $[123] !== showReplyAndQuote) {
    t37 = <WaveDropMobileMenu drop={drop} isOpen={effectiveIsSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onAddReaction={handleOnAddReaction} onEdit={handleOnEdit} onBoostAnimation={handleMobileBoostAnimation} />;
    $[116] = drop;
    $[117] = effectiveIsSlideUp;
    $[118] = handleMobileBoostAnimation;
    $[119] = handleOnAddReaction;
    $[120] = handleOnEdit;
    $[121] = handleOnReply;
    $[122] = longPressTriggered;
    $[123] = showReplyAndQuote;
    $[124] = t37;
  } else {
    t37 = $[124];
  }
  let t38;
  if ($[125] !== boostAnimation || $[126] !== handleBoostAnimationComplete) {
    t38 = <DropBoostAnimation animation={boostAnimation} onComplete={handleBoostAnimationComplete} />;
    $[125] = boostAnimation;
    $[126] = handleBoostAnimationComplete;
    $[127] = t38;
  } else {
    t38 = $[127];
  }
  let t39;
  if ($[128] !== drop.serial_no || $[129] !== drop.stableHash || $[130] !== dropClasses || $[131] !== handleTouchEnd || $[132] !== handleTouchMove || $[133] !== handleTouchStart || $[134] !== reactionsRow || $[135] !== t36 || $[136] !== t37 || $[137] !== t38) {
    t39 = <div ref={dropRef} className={dropClasses} data-wave-drop-id={drop.stableHash} data-serial-no={drop.serial_no} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} onTouchMove={handleTouchMove}>{t36}{reactionsRow}{t37}{t38}</div>;
    $[128] = drop.serial_no;
    $[129] = drop.stableHash;
    $[130] = dropClasses;
    $[131] = handleTouchEnd;
    $[132] = handleTouchMove;
    $[133] = handleTouchStart;
    $[134] = reactionsRow;
    $[135] = t36;
    $[136] = t37;
    $[137] = t38;
    $[138] = t39;
  } else {
    t39 = $[138];
  }
  let t40;
  if ($[139] !== t35 || $[140] !== t39) {
    t40 = <div className={t35}>{t39}</div>;
    $[139] = t35;
    $[140] = t39;
    $[141] = t40;
  } else {
    t40 = $[141];
  }
  return t40;
};
export default memo(WaveDrop);
function _temp(user) {
  return {
    mentioned_profile_id: user.mentioned_profile_id,
    handle_in_content: user.handle_in_content
  };
}
function _temp2(wave) {
  return {
    wave_id: wave.wave_id,
    wave_name_in_content: wave.wave_name_in_content
  };
}